import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ShieldCheck, X } from 'lucide-react';
import {
  ALL_HALT_ACTIONS,
  getHaltHistorySummary,
  getHaltStatus,
  listHaltHistory,
  resumeHalt,
  type HaltAction,
  type HaltHistoryItem,
  type HaltHistoryListResponse,
  type HaltHistorySummary,
  type HaltStatus,
} from '../../api/riskApi';
import { useRiskHalt } from '../../hooks/useRiskHalt';
import { ApiError } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import '../GovernanceLive/governanceLive.css';

// 권위: ARCH_DASHBOARD.md v1.8 §4.14.
// - Loss 원천 값은 표시하지 않음 (Non-Goals).
// - ADR-RISK-07 — 변동성 지수 트리거 폐기. 관련 카드/요약 컬럼 제거.
// - STOMP /topic/risk-halt 수신 시 배너/이력 즉시 갱신.
// - RESUME 은 2단계 확인 + reason 30자+.

const ACTION_LABEL: Record<HaltAction, string> = {
  none: 'NONE',
  halt_new_orders: 'HALT_NEW_ORDERS',
  reduce_all_50pct: 'REDUCE_ALL_50PCT',
  halt_all: 'HALT_ALL',
};

const ACTION_DESC: Record<HaltAction, string> = {
  none: '정상 모드. 모든 주문 경로 허용.',
  halt_new_orders: '신규 주문 차단. 기존 포지션 유지.',
  reduce_all_50pct: '전 포지션 즉시 50% 축소.',
  halt_all: '모든 매매 중단.',
};

function formatKst(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? '—';
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

function formatDuration(sec: number | null): string {
  if (sec === null || sec === undefined) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function RiskHaltPage(): JSX.Element {
  const qc = useQueryClient();
  const [actionFilter, setActionFilter] = useState<HaltAction[]>([]);
  const [resumeOpen, setResumeOpen] = useState(false);

  const statusQuery = useQuery<HaltStatus, ApiError>({
    queryKey: ['risk', 'halt-status'],
    queryFn: getHaltStatus,
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.halted) return 10_000;
      return 120_000;
    },
    staleTime: 5_000,
    retry: false,
  });

  const historyQuery = useQuery<HaltHistoryListResponse, ApiError>({
    queryKey: ['risk', 'halt-history', actionFilter],
    queryFn: () =>
      listHaltHistory({
        action: actionFilter.length > 0 ? actionFilter : undefined,
        page: 0,
        size: 50,
      }),
    staleTime: 30_000,
    retry: false,
  });

  const summaryQuery = useQuery<HaltHistorySummary, ApiError>({
    queryKey: ['risk', 'halt-history', 'summary', 7],
    queryFn: () => getHaltHistorySummary(7),
    staleTime: 60_000,
    retry: false,
  });

  // STOMP — halt 상태 전이 시 즉시 재조회.
  const onLive = useCallback(
    (ev: { event_code: string }) => {
      const code = ev.event_code;
      if (
        code.startsWith('RISK.HALT.') ||
        code.startsWith('RISK.POSITION.') ||
        code.startsWith('PORTFOLIO.')
      ) {
        qc.invalidateQueries({ queryKey: ['risk'] });
      }
    },
    [qc],
  );
  useRiskHalt(onLive);

  const status = statusQuery.data;
  const history = historyQuery.data?.items ?? [];
  const summary = summaryQuery.data;

  const toggleAction = (a: HaltAction): void => {
    setActionFilter((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  };

  // Resume mutation
  const resumeMut = useMutation({
    mutationFn: (body: { approver: string; reason: string; password: string }) =>
      resumeHalt(body),
    onSuccess: () => {
      setResumeOpen(false);
      qc.invalidateQueries({ queryKey: ['risk'] });
    },
  });

  return (
    <div className="gov-page">
      <header className="gov-page__header">
        <h1 className="gov-page__title">Risk Halt 상태</h1>
        <div className="gov-page__actions">
          <Link to="/live">Live Monitor</Link>
          <Link to="/logs">시스템 로그</Link>
        </div>
      </header>

      {statusQuery.isLoading && (
        <div className="gov-muted" style={{ padding: 24 }}>상태 조회 중…</div>
      )}
      {statusQuery.isError && (
        <div className="gov-error" role="alert">
          Halt 상태 조회 실패: {statusQuery.error?.message ?? 'unknown'}
        </div>
      )}

      {/* 상단 배너 */}
      {status && (
        <section
          className={`gov-banner ${status.halted ? 'gov-banner--danger' : 'gov-banner--ok'}`}
        >
          <div className="gov-banner__left">
            {status.halted ? (
              <AlertTriangle size={28} strokeWidth={1.75} color="var(--danger)" />
            ) : (
              <ShieldCheck size={28} strokeWidth={1.75} color="var(--ok)" />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h2 className="gov-banner__title">
                {status.halted
                  ? `HALTED — ${ACTION_LABEL[status.action]}`
                  : 'Normal — no active halt'}
              </h2>
              <div className="gov-banner__sub">
                {status.halted ? (
                  <>
                    since {formatKst(status.since)} · reason_code{' '}
                    <strong>{status.reason_code ?? '—'}</strong>
                  </>
                ) : (
                  <>last_resume_ready_at {formatKst(status.last_resume_ready_at)}</>
                )}
              </div>
              {status.halted && status.reason_detail && (
                <div className="gov-banner__sub">{status.reason_detail}</div>
              )}
            </div>
          </div>
          <div className="gov-banner__right">
            {status.halted && status.requires_operator_resume && (
              <button
                type="button"
                className="gov-btn gov-btn--danger"
                onClick={() => setResumeOpen(true)}
                aria-label="RESUME 승인"
              >
                RESUME 승인
              </button>
            )}
          </div>
        </section>
      )}

      {/* 섹션 A — Kill-Switch 액션 매트릭스 */}
      {status && (
        <section className="gov-card">
          <h2 className="gov-card__title">섹션 A · Kill-Switch 액션 매트릭스</h2>
          <div className="gov-action-matrix">
            {ALL_HALT_ACTIONS.map((a) => (
              <div
                key={a}
                className={`gov-action-cell${status.action === a ? ' gov-action-cell--active' : ''}`}
              >
                <span className="gov-action-cell__name">{ACTION_LABEL[a]}</span>
                <span className="gov-action-cell__desc">{ACTION_DESC[a]}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 섹션 B — 원인 이벤트 체인 */}
      {status && status.halted && (
        <section className="gov-card">
          <h2 className="gov-card__title">섹션 B · 원인 이벤트 체인</h2>
          <ul className="gov-timeline">
            {status.trigger_event_id && (
              <li className="gov-timeline__item gov-timeline__item--fatal">
                <Link to={`/history/${status.trigger_event_id}`}>
                  <strong>{status.reason_code}</strong>
                </Link>
                <div className="gov-timeline__meta">trigger · {formatKst(status.since)}</div>
              </li>
            )}
            {status.additional_triggers.map((code) => (
              <li key={code} className="gov-timeline__item gov-timeline__item--warn">
                <code>{code}</code>
                <div className="gov-timeline__meta">additional_trigger</div>
              </li>
            ))}
            {!status.trigger_event_id && status.additional_triggers.length === 0 && (
              <li className="gov-muted">원인 체인 없음.</li>
            )}
          </ul>
        </section>
      )}

      {/* 섹션 D — 임계 돌파 이벤트 요약 (7일) */}
      <section className="gov-card">
        <h2 className="gov-card__title">섹션 D · 임계 돌파 이벤트 요약 (최근 7일)</h2>
        <div className="gov-grid-4">
          <SummaryCell
            label="LOSS.THRESHOLD_BREACHED"
            value={summary?.loss_threshold_breached_7d}
            code="PORTFOLIO.LOSS.THRESHOLD_BREACHED"
          />
          <SummaryCell
            label="AGENT_DOWN"
            value={summary?.agent_down_7d}
            code="GOVERNANCE.AGENT.DOWN"
          />
          <SummaryCell
            label="BROKER.API"
            value={summary?.broker_api_7d}
            code="BROKER.API.TOTAL_FAILURE"
          />
          <SummaryCell
            label="DATA.FEED.STALE"
            value={summary?.data_feed_stale_7d}
            code="DATA.FEED.STALE"
          />
          <SummaryCell
            label="MARKET.CIRCUIT_BREAKER"
            value={summary?.market_circuit_breaker_7d}
            code="MARKET.CIRCUIT_BREAKER"
          />
        </div>
      </section>

      {/* 섹션 C — 최근 halt 이력 */}
      <section className="gov-card">
        <h2 className="gov-card__title">섹션 C · 최근 halt 이력 (50건)</h2>
        <div className="gov-filter" style={{ marginBottom: 8 }}>
          <div>
            <div className="gov-kpi__label" style={{ marginBottom: 4 }}>
              action 필터
            </div>
            <div className="gov-chip-row">
              {ALL_HALT_ACTIONS.filter((a) => a !== 'none').map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`gov-chip${actionFilter.includes(a) ? ' gov-chip--active' : ''}`}
                  onClick={() => toggleAction(a)}
                >
                  {ACTION_LABEL[a]}
                </button>
              ))}
            </div>
          </div>
        </div>
        {historyQuery.isError && (
          <div className="gov-error" role="alert">
            이력 조회 실패: {historyQuery.error?.message ?? 'unknown'}
          </div>
        )}
        <table className="gov-table">
          <thead>
            <tr>
              <th>triggered_at</th>
              <th>action</th>
              <th>trigger_event_code</th>
              <th>requires_resume</th>
              <th>recovered_at</th>
              <th>duration</th>
              <th>resumed_by</th>
            </tr>
          </thead>
          <tbody>
            {historyQuery.isLoading && (
              <tr>
                <td colSpan={7} className="gov-muted" style={{ padding: 16 }}>
                  로딩 중…
                </td>
              </tr>
            )}
            {!historyQuery.isLoading && history.length === 0 && (
              <tr>
                <td colSpan={7} className="gov-muted" style={{ padding: 16 }}>
                  최근 halt 이력이 없습니다.
                </td>
              </tr>
            )}
            {history.map((h: HaltHistoryItem) => (
              <tr
                key={h.trigger_event_id}
                style={
                  h.requires_operator_resume && !h.recovered_at
                    ? { background: 'var(--warn-soft)' }
                    : undefined
                }
              >
                <td>{formatKst(h.triggered_at)}</td>
                <td>
                  <code>{ACTION_LABEL[h.action]}</code>
                </td>
                <td>
                  <Link to={`/history/${h.trigger_event_id}`}>
                    <code>{h.trigger_event_code}</code>
                  </Link>
                </td>
                <td>{h.requires_operator_resume ? '✓' : '—'}</td>
                <td>{h.recovered_at ? formatKst(h.recovered_at) : '—'}</td>
                <td className="num">{formatDuration(h.duration_seconds)}</td>
                <td>{h.resumed_by ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {resumeOpen && (
        <ResumeModal
          onClose={() => setResumeOpen(false)}
          onConfirm={(reason, password) =>
            resumeMut.mutateAsync({
              approver: useAuthStore.getState().user?.email ?? 'operator',
              reason,
              password,
            })
          }
          busy={resumeMut.isPending}
          error={
            resumeMut.isError ? (resumeMut.error as ApiError | Error)?.message : null
          }
        />
      )}
    </div>
  );
}

function SummaryCell({
  label,
  value,
  code,
}: {
  label: string;
  value?: number;
  code: string;
}): JSX.Element {
  return (
    <Link
      to={`/logs?eventCode=${encodeURIComponent(code)}`}
      className="gov-kpi"
      style={{ textDecoration: 'none' }}
    >
      <span className="gov-kpi__label">{label}</span>
      <span className="gov-kpi__value">
        {value !== undefined ? `${value}회` : '—'}
      </span>
    </Link>
  );
}

// ── Resume 2단계 확인 모달 ──────────────────────────
function ResumeModal({
  onClose,
  onConfirm,
  busy,
  error,
}: {
  onClose: () => void;
  onConfirm: (reason: string, password: string) => Promise<unknown>;
  busy?: boolean;
  error?: string | null;
}): JSX.Element {
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (step === 1) reasonRef.current?.focus();
  }, [step]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const reasonOk = reason.trim().length >= 30;

  return (
    <div
      className="gov-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Halt 해제 (RESUME) 확인"
    >
      <div className="gov-modal">
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h2 className="gov-modal__title">
            {step === 1 ? 'Halt 해제 — 사유 입력' : 'Halt 해제 — 비밀번호 재입력'}
          </h2>
          <button
            type="button"
            className="gov-btn"
            onClick={onClose}
            disabled={busy}
            aria-label="닫기"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="gov-warning">
              주문 차단을 해제합니다. 원인 이벤트가 RECOVERED 상태인지 재확인 후 진행하세요.
            </div>
            <label>
              <span className="gov-modal__label">
                해제 사유 (30자 이상 필수 · {reason.trim().length}/30+)
              </span>
              <textarea
                ref={reasonRef}
                className="gov-modal__textarea"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="예: 브로커 API 장애 복구 확인, 신규 주문 1회 성공 확인 — 수동 해제"
                disabled={busy}
              />
            </label>
            <div className="gov-modal__actions">
              <button type="button" className="gov-btn" onClick={onClose} disabled={busy}>
                취소
              </button>
              <button
                type="button"
                className="gov-btn gov-btn--primary"
                disabled={!reasonOk || busy}
                onClick={() => setStep(2)}
              >
                다음
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="gov-warning">
              2단계 확인 — 운영자 비밀번호를 재입력하세요.
            </div>
            <label>
              <span className="gov-modal__label">비밀번호</span>
              <input
                type="password"
                className="gov-modal__input"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </label>
            {error && <div className="gov-error">{error}</div>}
            <div className="gov-modal__actions">
              <button type="button" className="gov-btn" onClick={() => setStep(1)} disabled={busy}>
                뒤로
              </button>
              <button
                type="button"
                className="gov-btn gov-btn--danger"
                disabled={!password || busy}
                onClick={() => onConfirm(reason.trim(), password).catch(() => {})}
              >
                {busy ? '해제 중…' : 'RESUME 확인'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
